/**
 * SSRelationshipGraph — radial / force-style ego-network visualization.
 *
 * Backed by GET /parties/{id}/graph?depth=N which returns:
 *   { nodes: [{ id, label, type, role_codes }], edges: [{ from, to, type, label }] }
 *
 * No external dependency — a deterministic radial layout per depth ring keeps
 * the bundle slim.  Click a node to recenter on it, drag the depth slider 1-3,
 * filter by relationship type with the chip strip.
 *
 * Props:
 *   centerId   number   — initial focus party
 *   onPick     fn(id)   — fires when the user clicks a node (parent recenters).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ssGraphAPI }                  from '../../../services/ssApi';
import { COLOR, RADIUS, FONT }         from '../../../components/ss/tokens';
import { Spinner, Button, Badge }      from '../../../components/ss';

const W = 700;
const H = 460;
const NODE_R = 22;
const RING_GAP = 110;

const TYPE_COLOR = {
  PER: COLOR.primary || '#3b82f6',
  ORG: '#8b5cf6',
  default: COLOR.grayMid || '#a99aa0',
};

// Deterministic radial layout — depth ring r determined by hop distance to
// center, angle by hash of node id so the picture is stable across renders.
function layoutRadial(nodes, edges, centerId) {
  const cx = W / 2;
  const cy = H / 2;

  // BFS depth from center
  const depth = { [centerId]: 0 };
  const queue = [centerId];
  const adj = new Map();
  for (const e of edges) {
    if (!adj.has(e.from)) adj.set(e.from, []);
    if (!adj.has(e.to))   adj.set(e.to, []);
    adj.get(e.from).push(e.to);
    adj.get(e.to).push(e.from);
  }
  while (queue.length) {
    const u = queue.shift();
    for (const v of adj.get(u) || []) {
      if (depth[v] === undefined) {
        depth[v] = depth[u] + 1;
        queue.push(v);
      }
    }
  }

  // Group by depth ring
  const rings = {};
  for (const n of nodes) {
    const d = depth[n.id] ?? 99;
    if (!rings[d]) rings[d] = [];
    rings[d].push(n);
  }

  const positions = new Map();
  for (const [d, ringNodes] of Object.entries(rings)) {
    const di = Number(d);
    const r = di * RING_GAP;
    const N = ringNodes.length || 1;
    ringNodes.sort((a, b) => String(a.id).localeCompare(String(b.id)));
    ringNodes.forEach((n, i) => {
      if (di === 0) {
        positions.set(n.id, { x: cx, y: cy });
      } else {
        const angle = (i / N) * 2 * Math.PI - Math.PI / 2;
        positions.set(n.id, { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      }
    });
  }
  return { positions, depth };
}

export default function SSRelationshipGraph({ centerId, onPick }) {
  const [depth, setDepth]       = useState(1);
  const [data,  setData]        = useState({ nodes: [], edges: [] });
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [typeFilter, setTypeFilter] = useState(new Set());
  const [hovered, setHovered]   = useState(null);

  // Fetch on mount + whenever centerId or depth changes.
  useEffect(() => {
    if (!centerId) return;
    setLoading(true);
    setErr('');
    ssGraphAPI.egoNetwork(centerId, depth, true)
      .then(res => {
        // Backend envelope unwrap: {success, data: {nodes, edges}} → data
        const payload = res?.data || res || {};
        setData({
          nodes: Array.isArray(payload.nodes) ? payload.nodes : [],
          edges: Array.isArray(payload.edges) ? payload.edges : [],
        });
      })
      .catch(e => setErr(e?.response?.data?.detail || 'Failed to load graph'))
      .finally(() => setLoading(false));
  }, [centerId, depth]);

  // Distinct relationship types for the filter chips.
  const allTypes = useMemo(() => {
    const set = new Set();
    for (const e of data.edges) if (e.type) set.add(e.type);
    return Array.from(set).sort();
  }, [data.edges]);

  // Filtered edges + the nodes that survive after edge filtering.
  const filtered = useMemo(() => {
    if (typeFilter.size === 0) return data;
    const edges = data.edges.filter(e => typeFilter.has(e.type));
    const keep = new Set([centerId]);
    for (const e of edges) { keep.add(e.from); keep.add(e.to); }
    return { nodes: data.nodes.filter(n => keep.has(n.id)), edges };
  }, [data, typeFilter, centerId]);

  const { positions } = useMemo(
    () => layoutRadial(filtered.nodes, filtered.edges, centerId),
    [filtered, centerId]
  );

  const toggleType = (t) => setTypeFilter(prev => {
    const next = new Set(prev);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: '100%' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={{ fontSize: 13, color: COLOR.textMuted, display: 'flex', alignItems: 'center', gap: 8 }}>
          Depth
          <input
            type="range" min={1} max={3} step={1}
            value={depth}
            onChange={(e) => setDepth(parseInt(e.target.value, 10))}
            style={{ width: 120 }}
          />
          <span style={{ fontWeight: 600, color: COLOR.text }}>{depth} hop{depth > 1 ? 's' : ''}</span>
        </label>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {allTypes.length === 0 ? null : (
            <span style={{ fontSize: 12, color: COLOR.textMuted, alignSelf: 'center' }}>Filter:</span>
          )}
          {allTypes.map(t => (
            <button
              key={t}
              onClick={() => toggleType(t)}
              style={{
                background:    typeFilter.has(t) ? COLOR.primary || '#3b82f6' : 'transparent',
                color:         typeFilter.has(t) ? '#fff' : COLOR.text,
                border:        `1px solid ${COLOR.grayBorder}`,
                borderRadius:  RADIUS.sm,
                padding:       '2px 8px',
                fontSize:      12,
                cursor:        'pointer',
              }}
            >{t}</button>
          ))}
          {typeFilter.size > 0 && (
            <Button variant="ghost" size="xs" onClick={() => setTypeFilter(new Set())}>Clear</Button>
          )}
        </div>

        <span style={{ marginLeft: 'auto', fontSize: 12, color: COLOR.textMuted }}>
          {filtered.nodes.length} nodes · {filtered.edges.length} edges
        </span>
      </div>

      {err && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 8, borderRadius: RADIUS.sm, fontSize: 13 }}>
          {err}
        </div>
      )}

      {/* SVG canvas */}
      <div style={{
        position: 'relative',
        border: `1px solid ${COLOR.grayBorder}`,
        borderRadius: RADIUS.md,
        background: COLOR.bg || '#fafafa',
        minHeight: H,
      }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner />
          </div>
        )}
        {!loading && filtered.nodes.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLOR.textMuted, fontSize: 13 }}>
            No relationships at depth {depth}.
          </div>
        )}

        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxWidth: '100%' }}>
          {/* Edges */}
          {filtered.edges.map((e, i) => {
            const a = positions.get(e.from);
            const b = positions.get(e.to);
            if (!a || !b) return null;
            const key = `${e.from}-${e.to}-${i}`;
            return (
              <g key={key}>
                <line
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={COLOR.grayMid || '#cbd5e1'}
                  strokeWidth={1.4}
                  opacity={0.7}
                />
                {(hovered === e.from || hovered === e.to) && e.label && (
                  <text
                    x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4}
                    fontSize={10} textAnchor="middle"
                    fill={COLOR.textMuted}
                  >{e.label}</text>
                )}
              </g>
            );
          })}

          {/* Nodes */}
          {filtered.nodes.map(n => {
            const p = positions.get(n.id);
            if (!p) return null;
            const isCenter = n.id === centerId;
            const fill = TYPE_COLOR[n.type] || TYPE_COLOR.default;
            return (
              <g
                key={n.id}
                transform={`translate(${p.x},${p.y})`}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHovered(n.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onPick?.(n.id)}
              >
                <circle
                  r={isCenter ? NODE_R + 4 : NODE_R}
                  fill={fill}
                  stroke={isCenter ? '#1e293b' : '#fff'}
                  strokeWidth={isCenter ? 3 : 2}
                />
                <text
                  y={4}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#fff"
                  pointerEvents="none"
                >
                  {n.type === 'ORG' ? '🏢' : (n.label?.[0] || '?').toUpperCase()}
                </text>
                <text
                  y={NODE_R + 16}
                  textAnchor="middle"
                  fontSize={11}
                  fill={COLOR.text}
                  pointerEvents="none"
                >
                  {(n.label || `#${n.id}`).slice(0, 18)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend */}
      <div style={{ marginTop: 10, display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: COLOR.textMuted }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: TYPE_COLOR.PER }} /> Person
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: TYPE_COLOR.ORG }} /> Organization
        </span>
        <span style={{ marginLeft: 'auto' }}>Click a node to recenter the graph.</span>
      </div>
    </div>
  );
}
